import type { DriverStatusChangeInput, UpdateDriverInput } from '@chukua/common';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { useToast } from '../../lib/use-toast';
import { changeDriverStatus, updateDriver } from './drivers.api';
import { useDriverDocuments } from './use-driver-documents';
import { useDriverRecord } from './use-driver-record';

/** A driver's own page: its record (`useDriverRecord`) plus every mutation the Details tab needs. Document mutations live in `use-driver-documents.ts`. */
export function useDriverDetail(id: string) {
  const { state, reload } = useDriverRecord(id);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function update(input: UpdateDriverInput): Promise<boolean> {
    setUpdateError(null);
    setSaving(true);
    try {
      await updateDriver(id, input);
      await reload();
      toast.success('Driver updated');
      return true;
    } catch (err) {
      setUpdateError(
        err instanceof ApiError && err.status === 409 ? 'A driver with this phone number already exists.' : 'Could not save — check the fields and try again.',
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  /** A row action with no form of its own: reload and toast on success, toast.error on failure (CLAUDE.md → Web conventions). */
  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<boolean> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
      return true;
    } catch {
      toast.error(errorMessage);
      return false;
    }
  }

  const changeStatus = (input: DriverStatusChangeInput) =>
    rowAction(() => changeDriverStatus(id, input), 'Driver status updated', "Couldn't update the driver's status");

  const documents = useDriverDocuments(id, reload);
  return { state, updateError, saving, update, changeStatus, ...documents };
}
